/**
 * Shared helpers for the Shopify App Store install handoff.
 *
 * When a merchant installs from the App Store they arrive at /onboarding/shopify
 * with a signed install token, but usually aren't logged in yet. The token is
 * stashed here so it survives the sign-in / sign-up detour, and the auth flows use
 * {@link resolvePostAuthRedirect} to send the merchant back to /onboarding/shopify
 * to finish claiming the install instead of the default landing page.
 */
const STORAGE_KEY = 'shopify_pending_install';

export interface PendingShopifyInstall {
    shop: string;
    installToken: string;
}

export function storePendingShopifyInstall(install: PendingShopifyInstall): void {
    const json = JSON.stringify(install);
    localStorage.setItem(STORAGE_KEY, json);
    sessionStorage.setItem(STORAGE_KEY, json);
}

export function getPendingShopifyInstall(): PendingShopifyInstall | null {
    const raw = sessionStorage.getItem(STORAGE_KEY) ?? localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    try {
        return JSON.parse(raw) as PendingShopifyInstall;
    } catch {
        return null;
    }
}

export function clearPendingShopifyInstall(): void {
    sessionStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
}

/**
 * Where to send the user after a successful sign-in / sign-up. Resumes a pending
 * Shopify install claim if one is waiting, otherwise falls back to `fallback`.
 */
export function resolvePostAuthRedirect(fallback = '/'): string {
    return getPendingShopifyInstall() ? '/onboarding/shopify' : fallback;
}
